import React from 'react';
import { useIsNewUser } from '@/hooks/useIsNewUser';
import { useUser } from '@/contexts/UserContext';
import { useIsMobile } from '@/hooks/use-mobile';

import { 
  getStandardTopPadding, 
  getStandardBottomPadding, 
  shouldApplyNewUserLayout 
} from '@/lib/layout-utils';

interface PageLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
  headerRight?: React.ReactNode;
  variant?: 'default' | 'recipe';
  maxWidth?: string;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  title,
  description,
  headerRight,
  variant = 'default',
  maxWidth = 'max-w-7xl',
}) => {
  const { isAuthenticated } = useUser();
  const { isNewUser, isLoading: newUserLoading } = useIsNewUser();
  const isMobile = useIsMobile();
  
  // Determine layout adjustments for new users
  const { showNewUserStyling, hasEmptyTitle } = shouldApplyNewUserLayout({
    isAuthenticated,
    isNewUser,
    newUserLoading,
    title
  });

  // Use standardized padding calculations
  const topPadding = getStandardTopPadding();
  const bottomPadding = getStandardBottomPadding({ 
    isAuthenticated, 
    isMobile 
  });

  // Get variant-specific header styling
  const getHeaderStyling = () => {
    if (variant === 'recipe') {
      return 'mb-4'; // Tighter margin for recipe pages
    }
    return 'mb-0'; // Default spacing
  };

  // Get variant-specific title styling
  const getTitleStyling = () => {
    if (variant === 'recipe') {
      return 'text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mt-1 md:mt-2';
    }
    return 'text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mt-2 md:mt-4';
  };

  return (
    <div className={`min-h-screen overflow-x-hidden bg-background relative ${topPadding}`}>
      <div className={`container px-3 md:px-4 mx-auto ${maxWidth}`}>
        <header className={`${getHeaderStyling()} relative page-header`}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-1 sm:pb-0">
            <div>
              <h1 className={getTitleStyling()}>{title}</h1>
              {description && (
                <p className={`text-muted-foreground text-sm md:text-base font-geist ${
                  showNewUserStyling ? 'mt-2 mb-6' : (variant === 'recipe' ? 'mt-1' : 'mt-2')
                }`}>{description}</p>
              )}
            </div>
            {headerRight && (
              <div className="mt-4 sm:mt-0">
                {headerRight}
              </div>
            )}
          </div>
        </header>
        <main className={`page-content ${bottomPadding}`}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default PageLayout;
